import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { usersAPI } from '../utils/api';
import { canManageUsers, getRoleDisplayName, getRoleBadgeColor, ROLES } from '../utils/permissions';
import { format } from 'date-fns';

export default function UsersManagement() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);

  useEffect(() => {
    // Check if user has permission to manage users
    if (!canManageUsers(user?.role)) {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.data.users);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      await usersAPI.updateRole(selectedUser.email, newRole);
      toast.success(`Role updated successfully for ${selectedUser.name}`);
      setShowRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleDeactivate = async (email) => {
    const confirmed = await confirm({
      title: 'Deactivate User',
      message: `Are you sure you want to deactivate ${email}? They will no longer be able to log in.`,
      confirmText: 'Deactivate',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await usersAPI.deactivate(email);
      toast.success(`User ${email} has been deactivated`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate user');
    }
  };

  const handleActivate = async (email) => {
    try {
      await usersAPI.activate(email);
      toast.success(`User ${email} has been activated`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to activate user');
    }
  };

  const openRoleModal = (userToEdit) => {
    setSelectedUser(userToEdit);
    setNewRole(userToEdit.role);
    setShowRoleModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/dashboard" className="text-2xl font-bold text-primary-600">
                Twilio Intake
              </Link>
            </div>
          </div>
        </nav>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="text-2xl font-bold text-primary-600">
              Twilio Intake
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-700 hover:text-gray-900">
                Dashboard
              </Link>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">{user?.name}</span>
                <button onClick={logout} className="btn btn-secondary btn-sm">
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600 mt-2">Manage user roles and access permissions</p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Users Table */}
        <div className="card">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {u.picture && (
                        <img
                          src={u.picture}
                          alt={u.name}
                          className="h-10 w-10 rounded-full mr-3"
                        />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{u.name}</div>
                        <div className="text-sm text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(u.role)}`}>
                      {getRoleDisplayName(u.role)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {u.is_active ? (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {u.last_login ? format(new Date(u.last_login), 'MMM d, yyyy h:mm a') : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openRoleModal(u)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Change Role
                      </button>
                      {u.email !== user?.email && (
                        <>
                          {u.is_active ? (
                            <button
                              onClick={() => handleDeactivate(u.email)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(u.email)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Activate
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Role Legend */}
        <div className="mt-6 card">
          <h3 className="text-lg font-semibold mb-3">Role Descriptions</h3>
          <dl className="space-y-2">
            <div>
              <dt className="font-medium text-gray-900">Administrator</dt>
              <dd className="text-gray-600 text-sm">Full access to all features including user management, deletion, and all operations</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-900">Agent</dt>
              <dd className="text-gray-600 text-sm">Can review, edit, add notes, submit to Twilio, approve/reject submissions (cannot delete or manage users)</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-900">Viewer</dt>
              <dd className="text-gray-600 text-sm">Read-only access to submissions and notes</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Role Change Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Change User Role</h3>
            <p className="text-sm text-gray-600 mb-4">
              Changing role for: <strong>{selectedUser?.name}</strong> ({selectedUser?.email})
            </p>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
            >
              <option value={ROLES.VIEWER}>Viewer</option>
              <option value={ROLES.AGENT}>Agent</option>
              <option value={ROLES.ADMIN}>Administrator</option>
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                  setNewRole('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleChangeRole} className="btn btn-primary">
                Update Role
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
